import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { DropdownOption, RefreshProjectProps, TranslationItem } from "../../types/screensTypes";
import { AudioAIDialogLine, AudioAI, FieldErrorsTranslation, Project, VoiceAI } from "../../types/types";
import { checkTranslationInProgress, createTranslations, getProject, getVoices } from "../../api";
import { getAudioDialogLinesBulk } from "../../api/audio";
import { getUserTranslations } from "../../api/translation";
import Dropdown from "../../components/common/Dropdown";
import Icon from "../../components/common/Icon";
import { useGlobalState } from "../../context/useGlobalState";
import Button from "../../components/ui/Button";
import CongratsAI from "../../components/modals/CongratsModal";
import AIGenerationStatusModal from "../../components/modals/AIGenerationStatusModal";
import MultiSelectDropdown from "../../components/common/MultiSelectDropdown";
import logo from "../../assets/images/logo-rewind.png";
import { useVoiceResolvers } from "../../hooks/useVoiceResolvers";
import TranslationQueueCounter from "../../components/translation/TranslationQueueCounter";
import { useRemainingTokens } from "../../hooks/useRemainingTokens";
import styles from "./Translation.module.css";
import studio from "../../components/modals/StudioModal.module.css";

interface TranslationProps extends RefreshProjectProps {}

interface TranslationColumn {
    language: string;
    selections: Record<string, string>;
}

// An audio with nothing written in it has nothing to translate, whatever the
// translation type: the backend skips it anyway. Same split as the backend:
// the dialog lines when there are any, the global text otherwise.
const hasTextToTranslate = (audio: AudioAI) => {
    const dialogLines = audio.dialogLines || [];
    return dialogLines.length > 0
        ? dialogLines.some((line) => !!(line.text && line.text.trim() !== ""))
        : !!(audio.text && audio.text.trim() !== "");
};

const Translation: React.FC<TranslationProps> = ({ refreshProjects, projects }) => {
    const [translationType, setTranslationType] = useState<"text" | "audio">("audio");
    const [translationColumns, setTranslationColumns] = useState<TranslationColumn[]>([]);
    const { data, loading, error } = useGlobalState();
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [selectedAudioOption, setSelectedAudioOption] = useState<string[]>(["ALL"]);
    const [projectData, setProjectData] = useState<Project | null>(null);
    const [loadingProject, setLoadingProject] = useState<boolean>(true);
    const [errorProject, setErrorProject] = useState<string | null>(null);
    const [voiceOptions, setVoiceOptions] = useState<VoiceAI[]>([]);
    const token = localStorage.getItem("token");
    const [loadingTranslation, setLoadingTranslation] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState<boolean>(true);
    const [hasProjectsGenerating, setHasProjectsGenerating] = useState<boolean>(false);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [hasFailedTranslation, setHasFailedTranslation] = useState<boolean>(false);
    const translationStartedAtRef = useRef<number | null>(null);
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState<boolean>(false);
    const [selectedLanguageForModal, setSelectedLanguageForModal] = useState<string>("");
    const [audioDialogLines, setAudioDialogLines] = useState<Record<string, AudioAIDialogLine[]>>({});
    const audioDialogLinesRef = useRef<Record<string, AudioAIDialogLine[]>>({});
    const [dialogLinesLoading, setDialogLinesLoading] = useState<boolean>(false);
    const dialogLinesRunIdRef = useRef<number>(0);
    const [queueRefreshTrigger, setQueueRefreshTrigger] = useState<number>(0);
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const { isTextExhausted, isAudioExhausted, refetch: refetchTokens, data: tokenData } = useRemainingTokens();
    const isQuotaExhausted = isTextExhausted || isAudioExhausted;

    const [fieldErrors, setFieldErrors] = useState<FieldErrorsTranslation>({
        selectedProject: false,
        selectedContentLanguage: false,
        selectedAudioOption: false,
        translations: false,
    });

    const fetchProject = async (id: string) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        if (!id) return;
        setErrorProject("");
        setLoadingProject(true);

        try {
            const response = await getProject({ token: token, projectId: id });
            setLoadingProject(false);

            if (!response || !response.id) {
                setErrorProject("Error loading project");
            } else {
                setProjectData(response);
            }
        } catch (error) {
            console.error("Error fetching project:", error);
            setLoadingProject(false);
            setErrorProject("Error loading project");
        }
    };

    const fetchVoices = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }
        const voiceAI = await getVoices({ token, language: "EN" });
        if (voiceAI) {
            setVoiceOptions(voiceAI.filter((voice) => voice.isActivated === true));
        } else {
            console.error("Failed to fetch voices");
        }
    };

    // Use centralized voice resolvers
    // For translations, we use resolveVoiceIdForTranslation which doesn't convert to voiceCloneDbId/promptVoiceDbId
    // The backend will handle the conversion to externalVoiceId for ElevenLabs
    const { resolveVoiceIdForTranslation, resolveVoiceIdFromClone, resolveVoiceIdFromPrompt } =
        useVoiceResolvers(voiceOptions);

    useEffect(() => {
        const translationsMissing =
            !translationColumns ||
            translationColumns.length === 0 ||
            (translationType === "audio" &&
                translationColumns.some((column) =>
                    Object.values(column.selections).some((value) => !value || value === ""),
                ));

        const errors: FieldErrorsTranslation = {
            selectedProject: !selectedProject,
            selectedContentLanguage: !selectedContentLanguage,
            selectedAudioOption: !selectedAudioOption || selectedAudioOption.length === 0,
            translations: translationsMissing,
        };

        setFieldErrors(errors);

        const hasErrors = Object.values(errors).some((error) => error);
        setIsGenerateButtonDisabled(hasErrors);
    }, [selectedProject, selectedContentLanguage, selectedAudioOption, translationColumns, translationType]);

    useEffect(() => {
        if (isGenerating && !hasProjectsGenerating) {
            if (hasFailedTranslation) {
                setShowErrorModal(true);
            } else {
                setIsModalOpen(true);
            }
            setIsGenerating(false);
            translationStartedAtRef.current = null;
        }
    }, [isGenerating, hasProjectsGenerating, hasFailedTranslation]);

    useEffect(() => {
        const checkQueue = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            const since = translationStartedAtRef.current
                ? new Date(translationStartedAtRef.current).toISOString()
                : undefined;
            const response = await checkTranslationInProgress({ token, since });
            if (!response) {
                console.error("Error checking queue status");
            } else {
                const hadProjectsGenerating = hasProjectsGenerating;
                if (response.queuedItem) {
                    setHasProjectsGenerating(true);
                    setIsGenerating(true);
                    if (!hadProjectsGenerating) {
                        setQueueRefreshTrigger((prev) => prev + 1);
                    }
                } else {
                    if (hadProjectsGenerating && translationStartedAtRef.current) {
                        setHasFailedTranslation(response.hasFailedTranslation ?? false);
                    }
                    setHasProjectsGenerating(false);
                    if (hadProjectsGenerating) {
                        refetchTokens();
                    }
                }
            }
        };
        checkQueue();
        const intervalId = setInterval(checkQueue, 10000);

        return () => clearInterval(intervalId);
    }, [token, hasProjectsGenerating, refetchTokens]);

    const showErrorMessage = () => {
        setShowErrors(true);
        setTimeout(() => {
            setShowErrors(false);
        }, 3000);
    };

    useEffect(() => {
        if (selectedProject && selectedProject !== "") {
            setSelectedAudioOption(["ALL"]);
            setSelectedContentLanguage(null as any);
            setTranslationColumns([]);
            fetchProject(selectedProject);
        }
    }, [selectedProject]);

    useEffect(() => {
        setTranslationColumns([]);
        setIsLanguageModalOpen(false);
    }, [selectedContentLanguage]);

    useEffect(() => {
        setTranslationColumns([]);
        setSelectedAudioOption(["ALL"]);
    }, [translationType]);

    useEffect(() => {
        fetchVoices();
    }, []);

    const handleOpenLanguageModal = () => {
        setSelectedLanguageForModal("");
        setIsLanguageModalOpen(true);
    };

    const handleCloseLanguageModal = () => {
        setSelectedLanguageForModal("");
        setIsLanguageModalOpen(false);
    };

    const filteredAudioOptions: DropdownOption[] = [
        { value: "ALL", label: t("all") },
        ...(projectData?.audioAI
            ?.filter((audio: AudioAI) => audio.language === selectedContentLanguage)
            ?.filter(hasTextToTranslate)
            ?.sort((a, b) => a.position - b.position)
            .map((audio: AudioAI) => ({
                value: audio.id,
                label: audio.name,
            })) || []),
    ];

    const languagesAIJsonFormatted: DropdownOption[] =
        data?.languagesAI?.map((language: any) => ({
            value: language.code,
            label: language.label,
        })) || [];

    const getLanguageLabel = (code: string) =>
        languagesAIJsonFormatted?.find((language) => language.value === code)?.label || code;

    const availableLanguagesForModal: DropdownOption[] =
        languagesAIJsonFormatted?.filter(
            (language) =>
                language.value !== selectedContentLanguage &&
                !translationColumns.some((column) => column.language === language.value),
        ) || [];

    const selectedAudioIds = useMemo(() => {
        if (!projectData) return [];
        if (!selectedAudioOption || selectedAudioOption.length === 0) return [];
        if (selectedAudioOption.includes("ALL")) {
            return projectData.audioAI?.filter(hasTextToTranslate).map((audio) => audio.id) || [];
        }
        return selectedAudioOption;
    }, [projectData, selectedAudioOption]);

    const sourceVoices = useMemo(() => {
        if (!projectData || !selectedContentLanguage) {
            return [];
        }

        const projectVoices = new Set<string>();
        const dialogVoices = new Set<string>(); // Track voices used in dialogs
        let hasAudioWithoutVoice = false;
        // Free-text voice names, from audios and replies that have no voice row of ours.
        // Each becomes its own mapping entry, so two named voices can be sent to two
        // different target voices instead of collapsing into "unknown voice".
        const voiceLabels = new Set<string>();

        projectData.audioAI?.forEach((audio: AudioAI) => {
            // Only process audios in the selected language
            if (audio.language !== selectedContentLanguage) {
                return;
            }

            // Only process selected audios
            if (!selectedAudioIds.includes(audio.id)) {
                return;
            }

            let audioHasVoice = false;

            // Helper to resolve and add voice ID (handles both voiceAIId and voiceCloneId).
            // Returns whether the voice can actually be mapped: one the company no longer
            // has activated is absent from the options list, so it gets no column of its
            // own and has to fall back on "unknown voice" rather than vanish.
            const addVoiceId = (voiceId: string | null | undefined, isDialogVoice: boolean = false): boolean => {
                if (!voiceId || voiceId.trim() === "") return false;
                // Skipped while the list is still loading: everything would look
                // unavailable for one render, flashing a spurious "unknown voice" row.
                if (voiceOptions.length > 0 && !voiceOptions.some((option) => option.id === voiceId)) return false;
                projectVoices.add(voiceId);
                if (isDialogVoice) {
                    dialogVoices.add(voiceId);
                }
                audioHasVoice = true;
                return true;
            };

            // Helper to resolve voiceCloneId/voicePromptId to VoiceAI ID
            const resolveAndAddVoice = (
                voiceAIId: string | null | undefined,
                voiceCloneId: string | null | undefined,
                voicePromptId: string | null | undefined = null,
                isDialogVoice: boolean = false,
            ): boolean => {
                if (voiceAIId && voiceAIId.trim() !== "") {
                    return addVoiceId(voiceAIId, isDialogVoice);
                } else if (voiceCloneId) {
                    const resolvedId = resolveVoiceIdFromClone(voiceCloneId);
                    return resolvedId ? addVoiceId(resolvedId, isDialogVoice) : false;
                } else if (voicePromptId) {
                    const resolvedId = resolveVoiceIdFromPrompt(voicePromptId);
                    return resolvedId ? addVoiceId(resolvedId, isDialogVoice) : false;
                }
                return false;
            };

            // A dialog can mix replies that have a voice with replies that do not — a line
            // standing for an imported audio, for instance. Tracked separately, because
            // `audioHasVoice` turns true on the first voice found and would then hide the
            // fact that some replies still need one.
            let hasVoicelessDialogLine = false;
            const audioLabel = (audio as any).voiceLabel as string | null | undefined;
            if (audioLabel && audioLabel.trim() !== "") voiceLabels.add(audioLabel.trim());

            if (audio.isDialog === true) {
                const dialogLines = audioDialogLines[audio.id];
                if (dialogLines && dialogLines.length > 0) {
                    dialogLines.forEach((dialogLine) => {
                        // Whether this reply ends up with a mapping column of its own.
                        let lineMapped = false;
                        // Check for voiceAIId first, then check if there's clone/prompt voice info in raw data
                        if (dialogLine.voiceAIId && dialogLine.voiceAIId.trim() !== "") {
                            lineMapped = addVoiceId(dialogLine.voiceAIId, true);
                        } else {
                            // Check if dialog line has clone/prompt voice info (might be in raw API response)
                            // The TypeScript interface might not include it, but the API might return it
                            const rawDialogLine = dialogLine as any;
                            if (rawDialogLine.voiceCloneId) {
                                const resolvedId = resolveVoiceIdFromClone(rawDialogLine.voiceCloneId);
                                if (resolvedId) {
                                    lineMapped = addVoiceId(resolvedId, true);
                                }
                            } else if (rawDialogLine.voiceClone?.voiceCloneDbId) {
                                // Check for nested voiceClone structure
                                const resolvedId = resolveVoiceIdFromClone(rawDialogLine.voiceClone.voiceCloneDbId);
                                if (resolvedId) {
                                    lineMapped = addVoiceId(resolvedId, true);
                                }
                            } else if (rawDialogLine.voicePromptId) {
                                // Check for prompt voice
                                const resolvedId = resolveVoiceIdFromPrompt(rawDialogLine.voicePromptId);
                                if (resolvedId) {
                                    lineMapped = addVoiceId(resolvedId, true);
                                }
                            } else if (rawDialogLine.voicePrompt?.id) {
                                // Check for nested voicePrompt structure
                                const resolvedId = resolveVoiceIdFromPrompt(rawDialogLine.voicePrompt.id);
                                if (resolvedId) {
                                    lineMapped = addVoiceId(resolvedId, true);
                                }
                            }
                        }

                        // No column of its own: either the reply names no voice at all,
                        // or it names one the company no longer has activated. Both need
                        // the "unknown voice" mapping, otherwise the passage is silently
                        // read by the voice mapped for the rest of the content.
                        const lineLabel = (dialogLine as any).voiceLabel as string | null | undefined;
                        if (lineLabel && lineLabel.trim() !== "") {
                            voiceLabels.add(lineLabel.trim());
                        } else if (!lineMapped) {
                            hasVoicelessDialogLine = true;
                        }
                    });
                } else {
                    // If no dialog lines are defined, use standard fields as fallback
                    resolveAndAddVoice(audio.voiceAIId, audio.voiceCloneId, audio.voicePromptId, true);
                    // Note: voiceAISecondId typically doesn't have a clone version, but check if it exists
                    if (audio.voiceAISecondId) {
                        addVoiceId(audio.voiceAISecondId, true);
                    }
                }
            } else {
                // For non-dialogs, use voiceAIId/voiceCloneId/voicePromptId and voiceAISecondId
                resolveAndAddVoice(audio.voiceAIId, audio.voiceCloneId, audio.voicePromptId, false);
                if (audio.voiceAISecondId) {
                    addVoiceId(audio.voiceAISecondId, false);
                }
            }

            // Track if we found an audio without any voice, or a dialog reply without one:
            // both need "unknown voice" to be offered, otherwise those passages silently
            // fall back to the content's primary voice with no way to choose.
            // A named voice is not a voiceless one: it has its own entry below.
            if ((!audioHasVoice && !audioLabel) || hasVoicelessDialogLine) {
                hasAudioWithoutVoice = true;
            }
        });

        // Filter voiceOptions to keep only project voices
        const voices = voiceOptions
            .filter((voice) => projectVoices.has(voice.id))
            .map((voice) => ({
                ...voice,
                isFromDialog: dialogVoices.has(voice.id), // Add flag to identify dialog voices
            }));

        // One entry per free-text voice name. The key is prefixed so it can never collide
        // with a real voice id, and the worker derives the same key from the stored name.
        voiceLabels.forEach((label) => {
            voices.push({
                id: `label:${label}`,
                name: label,
                value: `label:${label}`,
                label,
                description: "",
                isActivated: true,
                origin: "custom",
                createdAt: new Date(),
                updatedAt: new Date(),
                apiKey: null,
                voiceId: null,
                languageAIId: null,
                languageAI: null,
                isFromDialog: false,
            } as unknown as VoiceAI & { isFromDialog: boolean });
        });

        // Add "unknown voice" if there are audios without voices
        if (hasAudioWithoutVoice) {
            voices.push({
                id: "UNKNOWN_VOICE",
                name: t("unknownVoice") || "Unknown voice",
                value: "UNKNOWN_VOICE",
                label: t("unknownVoice") || "Unknown voice",
                description: "",
                isActivated: true,
                origin: "unknown",
                createdAt: new Date(),
                updatedAt: new Date(),
                apiKey: null,
                voiceId: null,
                languageAIId: null,
                languageAI: null,
                isFromDialog: false,
            } as unknown as VoiceAI & { isFromDialog: boolean });
        }

        return voices;
    }, [
        voiceOptions,
        projectData,
        selectedAudioIds,
        audioDialogLines,
        selectedContentLanguage,
        t,
        resolveVoiceIdFromClone,
        resolveVoiceIdFromPrompt,
    ]);

    const gridTemplateColumns = useMemo(() => {
        const columns = ["240px"];
        translationColumns.forEach(() => columns.push("280px"));
        return columns.join(" ");
    }, [translationColumns]);

    const chooseLanguageLabel = t("chooseLanguage", { defaultValue: t("selectALanguage") });
    const addLanguageLabel = t("addLanguage", { defaultValue: "Add language" });

    const ensureSelectionsForSourceVoices = (column: TranslationColumn): TranslationColumn => {
        const selections = { ...column.selections };
        let hasChange = false;

        sourceVoices.forEach((voice) => {
            if (!selections[voice.id]) {
                selections[voice.id] = "";
                hasChange = true;
            }
        });

        Object.keys(selections).forEach((voiceId) => {
            if (!sourceVoices.find((voice) => voice.id === voiceId)) {
                delete selections[voiceId];
                hasChange = true;
            }
        });

        return hasChange ? { ...column, selections } : column;
    };

    useEffect(() => {
        setTranslationColumns((prev) => prev.map((column) => ensureSelectionsForSourceVoices(column)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceVoices.map((voice) => voice.id).join("|")]);

    useEffect(() => {
        audioDialogLinesRef.current = audioDialogLines;
    }, [audioDialogLines]);

    useEffect(() => {
        if (!projectData) {
            setAudioDialogLines({});
            setDialogLinesLoading(false);
        }
    }, [projectData]);

    useEffect(() => {
        if (!token || !projectData) {
            setDialogLinesLoading(false);
            return;
        }

        const dialogAudios =
            projectData.audioAI?.filter(
                (audio: AudioAI) => audio.isDialog === true && selectedAudioIds.includes(audio.id),
            ) || [];

        const dialogAudioIds = dialogAudios.map((audio) => audio.id);
        const dialogAudioIdSet = new Set(dialogAudioIds);

        // Drop entries for audios that are no longer selected.
        setAudioDialogLines((prev) => {
            const next: Record<string, AudioAIDialogLine[]> = {};
            let changed = false;
            for (const audioId of Object.keys(prev)) {
                if (dialogAudioIdSet.has(audioId)) {
                    next[audioId] = prev[audioId];
                } else {
                    changed = true;
                }
            }
            return changed ? next : prev;
        });

        // Determine which ids still need fetching, using the ref so this effect
        // does NOT depend on audioDialogLines (which would create a re-run loop
        // and previously caused a fan-out storm of GET /audio/:id/dialog-lines
        // requests, exhausting Chrome's socket budget — ERR_INSUFFICIENT_RESOURCES).
        const missing = dialogAudioIds.filter((id) => audioDialogLinesRef.current[id] === undefined);
        if (missing.length === 0) {
            setDialogLinesLoading(false);
            return;
        }

        // Bump the run id so any in-flight loop from a previous effect run becomes
        // "stale" — its finally block won't clobber our loading state.
        const myRunId = ++dialogLinesRunIdRef.current;
        let cancelled = false;
        const CHUNK = 100;

        setDialogLinesLoading(true);

        (async () => {
            try {
                for (let i = 0; i < missing.length && !cancelled; i += CHUNK) {
                    const batch = missing.slice(i, i + CHUNK);
                    try {
                        const map = await getAudioDialogLinesBulk({ token, audioIds: batch });
                        if (cancelled) return;
                        setAudioDialogLines((prev) => {
                            const next = { ...prev };
                            for (const id of batch) {
                                if (next[id] === undefined) {
                                    next[id] = map[id] || [];
                                }
                            }
                            return next;
                        });
                    } catch (err) {
                        console.error("Error fetching dialog lines (bulk):", err);
                    }
                }
            } finally {
                // Only the most recent run is allowed to clear the loading flag.
                if (dialogLinesRunIdRef.current === myRunId) {
                    setDialogLinesLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [token, projectData, selectedAudioIds]);

    const getFilteredVoiceOptions = (sourceVoice: VoiceAI & { isFromDialog: boolean }) => {
        // If the source voice is from a dialog, only show ElevenLabs voices
        if (sourceVoice.isFromDialog) {
            return voiceOptions.filter((voice) => voice.origin === "elevenlabs");
        }
        // Otherwise, show all available voices
        return voiceOptions;
    };

    const handleVoiceSelection = (columnIndex: number, sourceVoiceId: string, voiceId: string) => {
        setTranslationColumns((prev) => {
            const updated = [...prev];
            const column = updated[columnIndex];
            const selections = {
                ...column.selections,
                [sourceVoiceId]: voiceId,
            };
            updated[columnIndex] = { ...column, selections };
            return updated;
        });
    };

    const handleAddLanguageToTranslations = () => {
        if (!selectedLanguageForModal) return;

        const initialSelections: Record<string, string> = {};
        sourceVoices.forEach((voice) => {
            initialSelections[voice.id] = "";
        });

        setTranslationColumns((prev) => [
            ...prev,
            { language: selectedLanguageForModal, selections: initialSelections },
        ]);
        setSelectedLanguageForModal("");
        setIsLanguageModalOpen(false);
    };

    const removeTranslation = (index: number) => {
        setTranslationColumns((prev) => prev.filter((_, idx) => idx !== index));
    };

    const generateTranslation = async () => {
        const translationsPayload = translationColumns.reduce<TranslationItem[]>((acc, column) => {
            if (translationType === "text") {
                acc.push({
                    language: column.language,
                    voice: "",
                    voiceSecond: "",
                    voiceMapping: {},
                });
                return acc;
            }

            const mappingEntries = Object.entries(column.selections).filter(
                ([, value]) => value !== undefined && value !== null && value !== "",
            );

            if (mappingEntries.length === 0) {
                return acc;
            }

            // For translations, send VoiceAI IDs directly (no conversion to voiceCloneDbId)
            // The backend will handle conversion to externalVoiceId for ElevenLabs
            const voiceMapping = mappingEntries.reduce<Record<string, string>>(
                (mappingAcc, [sourceVoiceId, targetVoiceId]) => {
                    // Use resolveVoiceIdForTranslation which sends VoiceAI ID as-is
                    const resolvedTargetVoiceId = resolveVoiceIdForTranslation(targetVoiceId);
                    if (resolvedTargetVoiceId) {
                        mappingAcc[sourceVoiceId] = resolvedTargetVoiceId;
                    }
                    return mappingAcc;
                },
                {},
            );

            // Resolve the first mapping voice ID for the main voice field
            const firstMappingRaw = mappingEntries[0]?.[1] || "";
            const firstMapping = resolveVoiceIdForTranslation(firstMappingRaw) || "";

            acc.push({
                language: column.language,
                voice: firstMapping,
                voiceSecond: "",
                voiceMapping,
            });

            return acc;
        }, []);

        const hasTranslations = translationsPayload.length > 0;

        const token = localStorage.getItem("token");
        if (!token) return;

        if (
            !selectedProject ||
            !selectedContentLanguage ||
            !selectedAudioOption ||
            selectedAudioOption.length === 0 ||
            !hasTranslations
        ) {
            setShowErrors(true);
            setTimeout(() => {
                setShowErrors(false);
            }, 3000);
            return;
        }
        setLoadingTranslation(true);
        const response = await createTranslations({
            token,
            projectId: selectedProject,
            language: selectedContentLanguage,
            selectedOption: selectedAudioOption,
            translationData: translationsPayload,
            translationType,
        });
        setLoadingTranslation(false);
        if (!response) {
            console.error("Error generating translations:");
        } else {
            translationStartedAtRef.current = Date.now();
            setHasFailedTranslation(false);
            setHasProjectsGenerating(true);
            setIsGenerating(true);
            // Fetch with a small delay after generation, then TranslationsQueue will poll every 5 seconds
            setTimeout(async () => {
                try {
                    await getUserTranslations({ token, page: 1, limit: 50 });
                    // Trigger refresh in the TranslationsQueue component
                    setQueueRefreshTrigger((prev) => prev + 1);
                } catch (error) {
                    console.error("Error fetching translations after generation:", error);
                    // Still trigger refresh even if there's an error
                    setQueueRefreshTrigger((prev) => prev + 1);
                }
            }, 1500);
        }
    };

    const handleAudioOptionChange = (selectedOptions: string[]) => {
        const lastSelected = selectedOptions[selectedOptions.length - 1];
        if (lastSelected === "ALL") {
            setSelectedAudioOption(["ALL"]);
        } else {
            const updatedOptions = selectedOptions.filter((option) => option !== "ALL");
            setSelectedAudioOption(updatedOptions);
        }
    };

    const redirectToProjectUrl = () => {
        if (selectedProject) {
            refreshProjects();
            navigate("/projects/" + selectedProject);
            setIsModalOpen(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <div className={styles.headTitle}>
                    <h1>{t("translation")}</h1>
                    <p className={styles.sub}>
                        {t("translateYourStories")}
                        <Tooltip title={t("doNotTranslateTooltip")} arrow>
                            <span style={{ display: "inline-flex" }}>
                                <Icon name="ic-info" />
                            </span>
                        </Tooltip>
                    </p>
                </div>
                {tokenData && (
                    <div className={styles.quota}>
                        {t("wordLeftForText")}{" "}
                        <b>{tokenData.remainigTextTokens?.toLocaleString?.() ?? tokenData.remainigTextTokens}</b>
                        <br />
                        {t("wordLeftForAudio")}{" "}
                        <b>{tokenData.remainigAudioTokens?.toLocaleString?.() ?? tokenData.remainigAudioTokens}</b>
                    </div>
                )}
            </div>

            <div className={styles.scroll}>
                <TranslationQueueCounter refreshTrigger={queueRefreshTrigger} />

                {/* Section 1 — Translation type */}
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>
                        <span className={styles.badge}>1</span>
                        {t("translationType")}
                    </h2>
                    <p className={styles.cardSub}>{t("chooseWhatYouWantToGenerate")}</p>
                    <div className={styles.typeGrid}>
                        {(["text", "audio"] as const).map((typeOption) => {
                            const isSelected = translationType === typeOption;
                            return (
                                <button
                                    key={typeOption}
                                    type="button"
                                    onClick={() => setTranslationType(typeOption)}
                                    className={`${styles.typeOpt}${isSelected ? ` ${styles.typeOptSel}` : ""}`}
                                >
                                    <span className={styles.typeIc}>
                                        <Icon name={typeOption === "text" ? "ic-file-text" : "ic-audio-lines"} />
                                    </span>
                                    <div className={styles.typeTxt}>
                                        <div className={styles.typeTt}>
                                            {typeOption === "text" ? t("textTranslation") : t("audioTranslation")}
                                        </div>
                                        <div className={styles.typeTd}>
                                            {typeOption === "text"
                                                ? t("textTranslationDescription")
                                                : t("audioTranslationDescription")}
                                        </div>
                                    </div>
                                    <span className={styles.radio}>
                                        <Icon name="ic-check" />
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Section 2 */}
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>
                        <span className={styles.badge}>2</span>
                        {t("whatDoYouWantToTranslate")}
                    </h2>
                    <p className={styles.cardSub}>{t("weCanTranslateAllOrPartOfAProject")}</p>

                    <div
                        className={`${styles.selectsRow}${
                            selectedProject && selectedContentLanguage ? ` ${styles.selectsRowThree}` : ""
                        }`}
                    >
                        <Dropdown
                            label=""
                            value={selectedProject}
                            options={projects}
                            onChange={setSelectedProject}
                            placeholder={t("selectAProject")}
                            isMissing={showErrors && fieldErrors.selectedProject}
                            loading={false}
                            error={false}
                            isWithoutMargin
                        />
                        <Dropdown
                            label=""
                            value={selectedContentLanguage}
                            options={languagesAIJsonFormatted}
                            onChange={setSelectedContentLanguage}
                            loading={loading}
                            error={error ? true : false}
                            placeholder={t("selectALanguage")}
                            isMissing={showErrors && fieldErrors.selectedContentLanguage}
                            isWithoutMargin
                        />
                        {selectedProject && selectedContentLanguage && (
                            <MultiSelectDropdown
                                label=""
                                options={filteredAudioOptions}
                                onChange={handleAudioOptionChange}
                                value={selectedAudioOption}
                                loading={loadingProject}
                                error={errorProject ? true : false}
                                placeholder={t("selectAnOption")}
                                isMissing={showErrors && fieldErrors.selectedAudioOption}
                                disabled={false}
                                isWithoutMargin
                            />
                        )}
                    </div>
                </div>

                {/* Section 3 */}
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>
                        <span className={styles.badge}>3</span>
                        {t("languages")}
                    </h2>

                    <p className={styles.cardSub}>{t("whatLanguageWouldYouLikeToTranslateYourProjectTo")}</p>

                    {translationType === "text" && (
                        <p className={`${styles.cardSub} ${styles.cardSubItalic}`}>{t("onlyTextWillBeTranslated")}</p>
                    )}

                    {translationType === "text" && (
                        <div className={styles.langPills}>
                            {translationColumns.map((column, columnIndex) => (
                                <div key={`${column.language}-pill-${columnIndex}`} className={styles.langPill}>
                                    <span>{getLanguageLabel(column.language)}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeTranslation(columnIndex)}
                                        className={styles.langPillRm}
                                        aria-label={t("remove")}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {translationType === "audio" && (
                        <div className={styles.langMapWrap}>
                            <div>
                                <div className={styles.langMap} style={{ gridTemplateColumns }}>
                                    <div className={styles.langColHead}>
                                        {t("sourceVoices", { defaultValue: "Source voices" })}
                                    </div>
                                    {translationColumns.map((column, columnIndex) => (
                                        <div
                                            key={`${column.language}-header-${columnIndex}`}
                                            className={`${styles.langColHead} ${styles.langColHeadBorder}`}
                                        >
                                            <span>{getLanguageLabel(column.language)}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeTranslation(columnIndex)}
                                                className={styles.langColRm}
                                                aria-label={t("remove")}
                                            >
                                                <Icon name="ic-x" />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {sourceVoices.length === 0 ? (
                                    <div className={styles.voiceEmpty}>{t("noVoicesAvailable")}</div>
                                ) : (
                                    <div className={styles.langMap} style={{ gridTemplateColumns }}>
                                        {sourceVoices.map((voice) => (
                                            <React.Fragment key={voice.id}>
                                                <Tooltip
                                                    title={
                                                        voice.isFromDialog
                                                            ? `${voice.name} (${t("dialogVoiceRestriction", { defaultValue: "Dialog voice, not all voices available" })})`
                                                            : voice.name
                                                    }
                                                    placement="top"
                                                >
                                                    <div className={styles.srcVoice}>{voice.name}</div>
                                                </Tooltip>
                                                {translationColumns.map((column, columnIndex) => (
                                                    <div
                                                        key={`${column.language}-${voice.id}`}
                                                        className={styles.voiceCell}
                                                    >
                                                        <Dropdown
                                                            label=""
                                                            options={getFilteredVoiceOptions(voice)}
                                                            onChange={(value: string) =>
                                                                handleVoiceSelection(columnIndex, voice.id, value)
                                                            }
                                                            loading={loading}
                                                            error={error ? true : false}
                                                            placeholder={t("selectAVoice")}
                                                            value={column.selections[voice.id]}
                                                            isMissing={
                                                                showErrors &&
                                                                (!column.selections[voice.id] ||
                                                                    column.selections[voice.id] === "")
                                                            }
                                                            isWithoutMargin
                                                        />
                                                    </div>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <button
                        type="button"
                        className={styles.addRound}
                        onClick={handleOpenLanguageModal}
                        disabled={
                            availableLanguagesForModal.length === 0 ||
                            !selectedContentLanguage ||
                            (translationType === "audio" && sourceVoices.length === 0)
                        }
                        aria-label={t("add") || "Add"}
                    >
                        <Icon name="ic-plus" />
                    </button>
                </div>

                {/* Launch Button */}
                <Tooltip
                    title={
                        isQuotaExhausted
                            ? t("quotaReachedTranslationTooltip")
                            : dialogLinesLoading
                              ? t("dialogLinesLoadingTooltip")
                              : ""
                    }
                    placement="top"
                >
                    <span>
                        <Button
                            onClick={() => generateTranslation()}
                            label={
                                loadingTranslation || hasProjectsGenerating ? t("generating") : t("startTranslation")
                            }
                            className={styles.startBtn}
                            disabled={
                                isGenerateButtonDisabled ||
                                loadingTranslation ||
                                hasProjectsGenerating ||
                                (translationType === "text" ? isTextExhausted : isQuotaExhausted) ||
                                (translationType === "audio" && dialogLinesLoading)
                            }
                            showError={showErrorMessage}
                        />
                    </span>
                </Tooltip>

                <div className={styles.note}>
                    <b>{t("importantSemicolon")}</b> {t("dependingOnTheVolumeOfText")}
                </div>
            </div>

            <CongratsAI
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                redirectToProjectUrl={redirectToProjectUrl}
                projectId={selectedProject}
            />

            <AIGenerationStatusModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                status="error"
                rank={null}
                errorTitle={t("audioGenerationFailed")}
                errorSubtitle={t("audioGenerationFailedSubtitle")}
            />

            {isLanguageModalOpen && (
                <div className={studio.overlayTop}>
                    <div className={`${studio.projectModalContent} ${styles.langModal}`}>
                        <div className={studio.logoWrap}>
                            <img src={logo} alt="Logo" />
                        </div>
                        <h1 className={studio.projectModalTitle}>{chooseLanguageLabel}</h1>
                        <p className={studio.projectModalSub}>
                            {t("addTargetLanguageForTranslation", {
                                defaultValue: "Ajoutez une langue cible pour votre traduction.",
                            })}
                        </p>

                        <div className={styles.langModalField}>
                            <Dropdown
                                label=""
                                value={selectedLanguageForModal}
                                options={availableLanguagesForModal}
                                onChange={(value: string) => setSelectedLanguageForModal(value)}
                                placeholder={t("selectALanguage")}
                                loading={loading}
                                error={false}
                                disabled={availableLanguagesForModal.length === 0}
                                fullWidth
                                isWithoutMargin
                            />
                        </div>

                        {availableLanguagesForModal.length === 0 && (
                            <p className={styles.langModalEmpty}>
                                {t("noMoreLanguagesAvailable", { defaultValue: "No more languages available." })}
                            </p>
                        )}

                        <div className={`${studio.buttonsCentered} ${styles.langModalActions}`}>
                            <Button
                                variant="primary"
                                shape="pill"
                                block
                                label={addLanguageLabel}
                                onClick={handleAddLanguageToTranslations}
                                disabled={!selectedLanguageForModal}
                            />
                            <Button
                                variant="cancel"
                                shape="pill"
                                block
                                label={t("close")}
                                onClick={handleCloseLanguageModal}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Translation;
